import { motion } from "framer-motion";
import type { CSSProperties, ElementType } from "react";

/**
 * AnimatedHeadline — word-by-word scroll-triggered rise.
 *
 * Extracted from the "Real attendees. Real experiences. Real building."
 * effect on event-detail.tsx (ReviewsAnimatedHeader). Each word slides
 * up from below the baseline through a clip-mask, staggered, when the
 * element scrolls into view. Fires once.
 *
 * Why this pattern (vs. opacity + y-translate): the per-word
 * `overflow: hidden` clip-mask + `y: 110% → 0` rise produces an
 * editorial "type setting itself" feel rather than a generic fade-up.
 *
 * Usage:
 *   <AnimatedHeadline as="h1" className="hero-h1" text="Some headline." />
 *   <AnimatedHeadline italicWord="Design" text="The Claude Design Workshop" />
 */
export interface AnimatedHeadlineProps {
  /** Headline text. Split on whitespace into animated word units. */
  text: string;
  /** Optional: a single word in the text to render with <em> italic styling. */
  italicWord?: string;
  /** Optional: CSS color (e.g. "var(--terra)") applied to the italicWord's <em>. */
  italicColor?: string;
  /** Optional: font-family applied to the italicWord's <em> (e.g. "var(--font-display)"). */
  italicFontFamily?: string;
  /** Optional: render the text on multiple lines by passing lines instead of text. */
  lines?: string[];
  className?: string;
  style?: CSSProperties;
  /** Tag to render as. Defaults to "h1". */
  as?: ElementType;
  /** Per-word stagger in seconds. */
  staggerSec?: number;
  /** Delay before the first word starts in seconds. */
  delaySec?: number;
}

export function AnimatedHeadline({
  text,
  italicWord,
  italicColor,
  italicFontFamily,
  lines,
  className,
  style,
  as: Tag = "h1",
  staggerSec = 0.08,
  delaySec = 0.15,
}: AnimatedHeadlineProps) {
  const linesArr = lines && lines.length > 0 ? lines : [text];

  // @react-three/fiber v9 augments the global JSX intrinsics, which widens
  // ElementType until <Tag> resolves its children prop to 'never'. Pinning the
  // prop shape here restores the ordinary HTML-tag contract.
  const Comp = Tag as React.ElementType<{ className?: string; style?: React.CSSProperties; children?: React.ReactNode }>;

  return (
    <Comp className={className} style={style}>
      <motion.span
        style={{ display: "block" }}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.35 }}
        variants={{
          hidden: {},
          visible: {
            transition: {
              staggerChildren: staggerSec,
              delayChildren: delaySec,
            },
          },
        }}
      >
        {linesArr.map((lineText, lineIdx) => {
          const words = lineText.split(/\s+/).filter(Boolean);
          return (
            <span
              key={lineIdx}
              style={{ display: linesArr.length > 1 ? "block" : "inline" }}
            >
              {words.map((word, i) => {
                const stripped = word.replace(/[^A-Za-z]/g, "").toLowerCase();
                const isItalic =
                  italicWord !== undefined &&
                  stripped === italicWord.toLowerCase();
                const isLast = i === words.length - 1;
                return (
                  <span
                    key={`${lineIdx}-${i}`}
                    style={{
                      display: "inline-block",
                      overflow: "hidden",
                      verticalAlign: "top",
                      padding: "0.08em 0.04em 0.18em",
                      margin: "-0.08em -0.04em -0.18em",
                    }}
                  >
                    <motion.span
                      style={{ display: "inline-block" }}
                      variants={{
                        hidden: { y: "110%" },
                        visible: {
                          y: "0%",
                          transition: { duration: 1.0, ease: [0.16, 1, 0.3, 1] },
                        },
                      }}
                    >
                      {isItalic ? (
                        <em
                          style={{
                            ...(italicColor ? { color: italicColor } : {}),
                            ...(italicFontFamily
                              ? { fontFamily: italicFontFamily }
                              : {}),
                          }}
                        >
                          {word}
                        </em>
                      ) : (
                        word
                      )}
                    </motion.span>
                    {!isLast && " "}
                  </span>
                );
              })}
            </span>
          );
        })}
      </motion.span>
    </Comp>
  );
}
